/* eslint-disable react/prop-types */
import React from 'react';
import {
  params,
  arrayOf as arrayOfType,
  shape as shapeType,
  string as stringType,
  useDataModel,
  extend,
  customType
} from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { Carousel } from '@one-thd/sui-carousel';
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  Typography,
  CardMedia,
  RatingMeter
} from '@one-thd/sui-atomic-components';
import { Price } from '@thd-olt-component-react/price';
import { ProductOverviewMobile as ProductOverview } from '@thd-olt-component-react/product-overview';
import { Specifications } from '@thd-olt-component-react/specifications';
import { RatingsAndReviewsCondensed as RatingsAndReviews } from '@thd-olt-component-react/ratings-and-reviews';
import { ProductSkeleton } from './ProductSkeleton';

const ProductInfo = ({
  itemId,
  setCanonicalUrl = () => { },
  showReview = false
}) => {
  const { storeId, membershipInformation } = useStore();
  const { data, loading, error } = useDataModel('product', {
    variables: {
      itemId,
      loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership,
    },
  });
  const product = data?.product || {};
  const productLabel = product?.identifiers?.productLabel || 'Product Label';
  const brandName = product?.identifiers?.brandName || 'Brand Name';
  const IMAGE_SIZE = 400;
  const images = product?.media?.images || [];
  const reviews = product?.reviews?.ratingsReviews || {};
  if (product?.identifiers?.canonicalUrl) {
    setCanonicalUrl(product.identifiers.canonicalUrl);
  }

  return (
    <>
      {loading && <ProductSkeleton />}
      {(!loading && !error) && (
        <section className="sui-flex sui-flex-col sui-gap-2">
          <Typography variant="h5">{brandName}</Typography>
          <Typography>{productLabel}</Typography>
          {!showReview && (
            <Typography>
              <RatingMeter
                edge="start"
                value={reviews?.averageRating}
                label={reviews?.averageRating !== 0 ? `(${reviews?.totalReviews})` : ''}
                RatingProps={{ color: 'brand' }}
              />
            </Typography>
          )}
          <div id="product-info-image-carousel" className="sui-mt-2 sui-relative">
            <Carousel layout="full-bleed" type="peekless" containerId="product-info-image-carousel">
              {images.map((image, i) => (
                <CardMedia
                  key={`hero-image-${i}`}
                  lazy
                  aspect="square"
                  src={image?.url?.replace('<SIZE>', IMAGE_SIZE)}
                  alt={image?.type}
                  width="fit"
                />
              ))}
            </Carousel>
          </div>
          <Price
            large
            itemId={itemId}
            storeId={storeId}
            displayEachUom={false}
            basic
            disableRangePricing
            isSignInToolTip
          />
          <hr className="sui-mt-2 sui-mb-0" />
          <div>
            <Accordion noBorders>
              <AccordionHeader aria-controls="product-info-overview" id="product-info-overview">
                Overview
              </AccordionHeader>
              <AccordionBody>
                <div className="sui-pb-4">
                  <ProductOverview itemId={itemId} isMiniPip />
                </div>
              </AccordionBody>
            </Accordion>
            <hr className="sui-mb-2" />
            <Accordion noBorders>
              <AccordionHeader aria-controls="product-info-specifications" id="product-info-specifications">
                Specifications
              </AccordionHeader>
              <AccordionBody>
                <Specifications itemId={itemId} hideMobileFade oneColumn hideSpecificationHeader />
              </AccordionBody>
            </Accordion>
            {showReview && (
              <>
                <hr className="sui-mb-2" />
                <Accordion noBorders>
                  <AccordionHeader aria-controls="product-info-reviews-ratings" id="product-info-reviews-ratings">
                    <Typography>
                      <span className="sui-text-lg sui-font-bold sui-pr-2">Reviews</span>
                      <RatingMeter
                        edge="start"
                        value={reviews?.averageRating}
                        label={reviews?.averageRating ? `(${reviews?.totalReviews})` : ''}
                        RatingProps={{ color: 'brand' }}
                      />
                    </Typography>
                  </AccordionHeader>
                  <AccordionBody>
                    <RatingsAndReviews itemId={itemId} />
                  </AccordionBody>
                </Accordion>
              </>
            )}
          </div>
        </section>
      )}
    </>
  );
};

ProductInfo.propTypes = {};

ProductInfo.displayName = 'ProductInfo';

ProductInfo.dataModel = extend(
  {
    product: params({
      itemId: stringType().isRequired(),
      loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
        svocID: stringType(),
        programTiers: arrayOfType(
          shapeType({
            tier: stringType(),
            program: stringType(),
          }),
        ),
      })
    }).shape({
      itemId: stringType(),
      dataSources: stringType(),
      identifiers: shapeType({
        brandName: stringType(),
        productLabel: stringType(),
        canonicalUrl: stringType()
      }),
      media: shapeType({
        images: arrayOfType(shapeType({
          url: stringType(),
        })),
      }),
      reviews: shapeType({
        ratingsReviews: shapeType({
          averageRating: stringType(),
          totalReviews: stringType()
        })
      })
    })
  },
  Price,
  ProductOverview,
  Specifications,
  RatingsAndReviews
);

export { ProductInfo };
